import {
  FluidDisplayDensity,
  FluidMenuItem,
  FluidMenuState,
  FluidMenuType,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  FluidThemePalette,
  FluidTreeMenuConfig,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  readTask,
  State,
  Watch,
} from '@stencil/core';

import { Nav, NavItem } from '../../common/fluid-navigation.components';
import {
  TREE_MENU_DISABLED_ID,
  TREE_MENU_INVALID_ID,
} from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import { TreeMenuItem } from './components/fluid-tree-menu.components';
import {
  getMenuItemById,
  getSelectedItemPath,
  hasSubmenu,
  initializeTreeMenuState,
  preventLinkDefault,
} from './utils/fluid-tree-menu.utils';

/**
 * @displayName Tree Menu
 *
 * @contextData FLUID Tree Menu is a hierarchical list of menu items with optional expandable/collapsible subsections. It is primarily used for navigation, but can also be used to display any structured collection of FluidMenuItem objects (e.g. file directories, locations, etc.).
 */
@Component({
  tag: 'fluid-tree-menu',
  styleUrl: 'fluid-tree-menu.scss',
  shadow: true,
})
export class FluidTreeMenu implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * If true, all @Watch methods are temporarily disabled to allow
   * "Utils.selectiveMapConfigToLocal()" to run without triggering
   * unintended re-renders.
   */
  private _disableWatch = false;

  /**
   * Flag used to determine if Tree Menu should auto-scroll to the
   * selected menu item after the current render cycle completes.
   */
  private _autoScrollAfterUpdate = false;

  /**
   * Reference to the currently selected menu item's HTMLElement.
   * Used by "scrollSelectedItemIntoView()".
   */
  _selectedItemRef: HTMLElement;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidTreeMenuElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: { content?: string };
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Derived Tree Menu state containing copy of "menuItems" array,
   * flattened array of all menu items containing submenus, and reference
   * to the currently selected menu item / submenu button.
   */
  @State() _treeMenuState: FluidMenuState;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * When provided, adjusts the current theme's color palette & appearance.
   * NOTE: a theme palette acts as a "style override" applied ON TOP of
   * the current theme & isn't necessary for "standard" theme aesthetics.
   */
  @Prop({ mutable: true }) themePalette: FluidThemePalette;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Array of FluidMenuItems that populates the Tree Menu.
   */
  @Prop({ mutable: true }) menuItems: FluidMenuItem[] = [];

  /**
   * Adjusts menu item size, padding, & spacing to be more or less compact.
   * For example, "compact" density increases the number of menu items shown
   * by ~30% compared to "default" density. NOTE: it is recommended to use
   * "default" density for Tree Menu implementations within FLUID Sidebar.
   */
  @Prop({ mutable: true }) displayDensity: FluidDisplayDensity =
    FluidDisplayDensity.DEFAULT;

  /**
   * Sets how menu item labels handle text overflow. By default, a menu item
   * label will wrap its text if the width is greater than the width of the Tree
   * Menu (this should be used in the majority of use cases for best User
   * Experience). Setting this Prop to "clip" will truncate text overflow
   * mid-character, and "ellipsis" will truncate text overflow with "..." (these
   * values should only be used when seeing the entire menu item label text is
   * NOT necessary to understand the menu item's context/purpose). Both "clip" &
   * "ellipsis" values will display a tooltip on :hover containing the full,
   * unedited menu item label.
   */
  @Prop({ mutable: true }) textOverflow: FluidTextOverflow =
    FluidTextOverflow.WRAP;

  /**
   * When true, submenu buttons can be selected like regular menu items.
   * If false (default), submenu buttons can only be used to expand/
   * collapse their child submenus. NOTE: if this Prop is false and
   * "collapsibleSubmenus" Prop is also false, the "submenuButtonClick"
   * event will NOT be emitted when submenu buttons are clicked (unless
   * FluidMenuItem "submenuCollapsible" property override is used).
   */
  @Prop({ mutable: true }) selectableSubmenuButtons = false;

  /**
   * When true (default), submenus can be expanded/collapsed using the
   * parent submenu button. If false, all submenus will be rendered
   * open by default & can't be expanded/collapsed. This Prop can
   * be overridden for individual FluidMenuItems using the
   * "submenuCollapsible" property.
   */
  @Prop({ mutable: true }) collapsibleSubmenus = true;

  /**
   * Sets Tree Menu styles & functionality. "default" & "sidebar" types
   * look & behave as a default Tree Menu. "dropdown" type is styled to
   * be embedded within a dropdown menu (i.e. similar to Context Menu
   * links) & does not display the current "selected" menu item.
   */
  @Prop({ mutable: true }) menuType: FluidMenuType = FluidMenuType.DEFAULT;

  /**
   * When true, Tree Menu will automatically scroll to the selected
   * menu item (if offscreen) when it is programmatically changed (via
   * "menuItems" Prop update or "setSelectedItem()" method) OR when
   * Sidebar is opened (if using built-in Tree Menu). NOTE: this
   * Prop has no effect when the user manually selects a menu item,
   * as the selected item will already be visible onscreen.
   */
  @Prop({ mutable: true }) autoScrollSelectedItem = false;

  /**
   * Allows Tree Menu local Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidTreeMenuConfig;

  @Watch('config')
  onConfigChanged() {
    const existingMenuItems = this.menuItems;
    const existingCollapsibleSubmenus = this.collapsibleSubmenus;
    Utils.selectiveMapConfigToLocal(this, this.config);

    switch (true) {
      case !Utils.isEqual(this.menuItems, existingMenuItems):
      case this.collapsibleSubmenus !== existingCollapsibleSubmenus:
        this.initializeTreeMenuState();
        this._autoScrollAfterUpdate = true;
        break;
    }
  }

  /**
   * Re-initialize _treeMenuState when Props updated.
   */
  @Watch('menuItems')
  @Watch('collapsibleSubmenus')
  handlePropChange(newValue, oldValue) {
    // Don't run if selectiveMapConfigToLocal() is running OR if values are equal
    if (this._disableWatch || Utils.isEqual(newValue, oldValue)) return;
    this.initializeTreeMenuState();
    this._autoScrollAfterUpdate = true;
  }

  // ================================================================ //
  // -- Events

  /**
   * Event fired when a menu item with no submenu is clicked. Contains
   * the FluidMenuItem that was clicked.
   */
  @Event() menuItemClick: EventEmitter<FluidMenuItem>;

  /**
   * Event fired when a menu item with a submenu is clicked. Contains
   * the FluidMenuItem that was clicked.
   */
  @Event() submenuButtonClick: EventEmitter<FluidMenuItem>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: selectiveMapConfigToLocal - set local @Props to "config" values
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config);
    Utils.setGlobalTheme(this);
    useI18n.bind(this)();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: initializeTreeMenuState() - initialize derived Tree Menu state
   */
  componentWillLoad(): void {
    this.setMenuType();
    this.initializeTreeMenuState();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action: re-enable @Watch methods (if disabled by "selectiveMapConfigToLocal()")
   * @action: updateParentScrollContainerRef - used by scrollSelectedItemIntoView()
   * @action: scrollSelectedItemIntoView - scrolls selected item into view (if necessary)
   */
  componentDidLoad(): void {
    this._disableWatch = false;
    this.updateParentScrollContainerRef();
    this.autoScrollSelectedItem && this.scrollSelectedItemIntoView();
  }

  /**
   * Called just after the component updates. It's never called during the first render().
   * @action: re-enable @Watch methods (if disabled by "selectiveMapConfigToLocal()")
   * @action: scrollSelectedItemIntoView - scrolls selected item into view (if necessary)
   */
  componentDidUpdate(): void {
    this._disableWatch = false;

    if (this.autoScrollSelectedItem && this._autoScrollAfterUpdate) {
      this._autoScrollAfterUpdate = false;
      this.scrollSelectedItemIntoView();
    }
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Returns the current derived Tree Menu state.
   */
  @Method()
  async getTreeMenuState() {
    return this._treeMenuState;
  }

  /**
   * Returns the currently selected menu item.
   */
  @Method()
  async getSelectedItem() {
    return this._treeMenuState?.selectedItem;
  }

  /**
   * Returns an array of menu item(s) that constitutes a "consecutive path"
   * to the currently selected menu item. For example, if the selected menu
   * item is nested within a submenu, "selectedItemPath" will consist of the
   * selected item's parent menu item (index 0) & the selected item (index 1).
   * Can be used to populate FLUID Breadcrumb's "trail" Prop.
   */
  @Method()
  async getSelectedItemPath() {
    return this._treeMenuState?.selectedItemPath;
  }

  /**
   * Returns flattened array of all menu items that contain submenus.
   */
  @Method()
  async getFlattenedSubmenuArray() {
    return this._treeMenuState?.submenuItems;
  }

  /**
   * Sets the selected menu item based on the "id" provided. If no "id" is
   * provided or "id" is null, unselects the currently selected menu item.
   * NOTE: all menu items must have an "id" property for this method to work
   * correctly (unless unselecting currently selected menu item).
   * @param menuItemId - "id" property of menu item to be selected or null (optional)
   * @param disableAutoScroll - prevents auto-scrolling to selected item when true (only relevant when "autoScrollSelectedItem" Prop is true)
   */
  @Method()
  async setSelectedItem(menuItemId?: string | null, disableAutoScroll = false) {
    if (isNullOrUndefined(this._treeMenuState)) return;

    const targetItem = getMenuItemById(
      this._treeMenuState.menuItems,
      menuItemId
    );

    // If "menuItemId" was provided but doesn't match any menu item, output warning & terminate
    if (!isNullOrUndefined(menuItemId) && isNullOrUndefined(targetItem)) {
      Utils.consoleWarn(this.host, TREE_MENU_INVALID_ID(menuItemId));
      return;
    }
    // If "id" matches a disabled menu item, output warning & terminate
    if (targetItem?.disabled) {
      Utils.consoleWarn(this.host, TREE_MENU_DISABLED_ID(menuItemId));
      return;
    }

    this.updateSelectedItem(targetItem);
    this._autoScrollAfterUpdate = !disableAutoScroll;
    this._treeMenuState.selectedItemPath?.forEach((item) => {
      hasSubmenu(item) && (item.submenuOpen = true);
    });
    this.refresh();

    return targetItem;
  }

  /**
   * Scrolls the currently selected menu item into view (if not already
   * visible). Requirements for scrolling to occur: a menu item must be
   * selected, the selected item must NOT be hidden within a collapsed
   * submenu, & the selected item must be outside the visible bounds
   * of the viewport.
   */
  @Method()
  async scrollSelectedItemIntoView() {
    try {
      // Wait for next render cycle to complete
      await Utils.nextTick();
      // Wait for all animations to complete (e.g. submenu expand/collapse)
      await Utils.componentAnimationEnd(this.host);

      if (
        isNullOrUndefined(this._treeMenuState?.selectedItem) ||
        !this._selectedItemRef ||
        !Utils.checkVisibility(this._selectedItemRef) ||
        Utils.elementIsOnscreen(
          this._selectedItemRef,
          this.parentScrollContainerRef
        )
      )
        return;

      this._selectedItemRef?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    } catch (_) {
      // Ignore error - it's due to multiple Method calls & won't affect functionality
    }
  }

  /**
   * Toggles all Tree Menu submenus open or closed.
   * @param open - boolean indicating whether submenus should be open or closed
   */
  @Method()
  async toggleSubmenus(open = false) {
    if (isNullOrUndefined(this._treeMenuState)) return;
    this._treeMenuState.submenuItems?.forEach((item) => {
      !item.disabled && item.submenuCollapsible && (item.submenuOpen = open);
    });
    this.refresh();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Sets the parent "scroll container" element (i.e. the scrollable
   * element that contains the Tree Menu) if not already defined. See
   * "parentScrollContainerRef" Prop documentation for details.
   */
  updateParentScrollContainerRef(): void {
    if (
      this.parentScrollContainerRef ||
      this.menuType !== FluidMenuType.SIDEBAR
    )
      return;

    readTask(() => {
      Utils.nextTick().then(() => {
        this.parentScrollContainerRef = Utils.closestAncestor(
          this.host,
          '.sidebar-content'
        ) as HTMLElement;
      });
    });
  }

  /**
   * Re-renders Tree Menu.
   */
  refresh(): void {
    this._treeMenuState = { ...this._treeMenuState };
  }

  /**
   * Initializes derived Tree Menu state & sets parent container for
   * Popovers.
   */
  initializeTreeMenuState(): void {
    this._treeMenuState = initializeTreeMenuState(this);
  }

  /**
   * Local method for storing "consecutive path" to selected item in
   * Tree Menu state.
   */
  updateSelectedItemPath(): void {
    this._treeMenuState.selectedItemPath = getSelectedItemPath(
      this._treeMenuState.menuItems
    );
  }

  /**
   * Determines if a submenu button should be treated as a non-interactive
   * / non-clickable label for its submenu (rather than an expand/collapse
   * toggle or a selectable menu item).
   * @param menuItem - the menu item associated with the submenu button in question
   */
  submenuButtonInactive(menuItem: FluidMenuItem): boolean {
    return !this.selectableSubmenuButtons && !menuItem.submenuCollapsible;
  }

  /**
   * Determines if a "title" attribute should be added to a menu item's label
   * that will display a tooltip with the full label text on :hover. Returns true
   * if the label text is set to truncate on overflow, or false if label text wraps.
   * @param menuItem - the menu item to potentially receive a "title" attribute
   */
  displayTitleAttr(menuItem: FluidMenuItem): boolean {
    switch (true) {
      case !!menuItem.textOverflow &&
        menuItem.textOverflow !== FluidTextOverflow.WRAP:
        return true;
      case !!menuItem.textOverflow &&
        menuItem.textOverflow === FluidTextOverflow.WRAP:
        return false;
      case this.textOverflow !== FluidTextOverflow.WRAP:
        return true;
      default:
        return false;
    }
  }

  /**
   * Expands or collapses a submenu if submenus are collapsible.
   * @param menuItem - the menu item containing the submenu to expand/collapse
   */
  toggleSubmenu(menuItem: FluidMenuItem): void {
    if (!menuItem.submenuCollapsible) return;
    menuItem.submenuOpen = !menuItem.submenuOpen;
  }

  /**
   * Sets a new selected menu item & replaces the old selected menu item in the
   * Tree Menu state. Called when regular menu item or submenu button is clicked.
   * Only triggered if menu item is NOT disabled, or submenu button is allowed
   * to be selected.
   * @param menuItem - the menu item to be selected
   */
  updateSelectedItem(menuItem: FluidMenuItem): void {
    if (
      menuItem?.disabled ||
      (hasSubmenu(menuItem) && !this.selectableSubmenuButtons)
    )
      return;

    if (this._treeMenuState.selectedItem) {
      this._treeMenuState.selectedItem.selected = false;
    }
    !isNullOrUndefined(menuItem) && (menuItem.selected = true);
    this._treeMenuState.selectedItem = menuItem;
    this.updateSelectedItemPath();
  }

  /**
   * Updates selected item in Tree Menu state, then emits "menuItemClick" event
   * containing the newly selected menu item. Called each time a menu item is clicked.
   * Also prevents the default behavior of <a> menu items.
   * @param menuItem - the menu item that was clicked
   * @param event - the click event to be prevented (if menu item is anchor element)
   */
  handleMenuItemClick(menuItem: FluidMenuItem, event: MouseEvent): void {
    preventLinkDefault(menuItem, event);
    if (menuItem.disabled) return;

    this.updateSelectedItem(menuItem);
    this.menuItemClick.emit(menuItem);
    this.refresh();
  }

  /**
   * Toggles submenu open/closed and/or sets submenu button to be selected (if
   * submenu buttons are allowed to be selected). Called each time a submenu button
   * is clicked, UNLESS it's disabled or "inactive" (see "submenuButtonInactive()").
   * @param menuItem - menu item associated with the submenu button that was clicked
   */
  handleSubmenuButtonClick(menuItem: FluidMenuItem): void {
    if (menuItem.disabled || this.submenuButtonInactive(menuItem)) return;

    this.updateSelectedItem(menuItem);
    this.toggleSubmenu(menuItem);
    this.submenuButtonClick.emit(menuItem);
    this.refresh();
  }

  /**
   * Stores a reference to the currently selected menu item's HTMLElement.
   * Called each time a menu item is rendered, but only sets "_selectedItemRef"
   * if the menu item is selected. Used by "scrollSelectedItemIntoView()" to
   * determine if the selected item is visible.
   * @param el - HTMLElement reference to a rendered menu item
   * @param menuItem - FluidMenuItem object used to render the menu item
   */
  setSelectedItemRef(el: HTMLElement, menuItem: FluidMenuItem): void {
    if (!el || !menuItem?.selected || this.menuType === FluidMenuType.DROPDOWN)
      return;
    this._selectedItemRef = el;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    if (!this._treeMenuState?.menuItems?.length) return;

    return (
      <Nav
        aria-label='Secondary'
        class={{
          [`fluid-tree-menu theme-${this.theme}`]: true,
          [`theme-palette-${this.themePalette}`]: !!this.themePalette,
          [`display-density-${this.displayDensity}`]: true,
          [`text-overflow-${this.textOverflow}`]: true,
          [`${this.menuType}-menu`]: true,
        }}>
        {this._treeMenuState.menuItems.map((item: FluidMenuItem) => (
          <NavItem class='tree-menu-nav-item'>
            <TreeMenuItem
              host={this}
              menuItem={item}
              i18nStore={this._i18nStore}
            />
          </NavItem>
        ))}
      </Nav>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * When using "autoScrollSelectedItem" Prop or "scrollSelectedItemIntoView()",
   * sets the parent "scroll container" (i.e. the scrollable element that
   * contains the Tree Menu) used to determine if the selected item is
   * visible onscreen. This prevents "fixed position" elements (such as
   * FLUID Header) from giving false positives when checking if the selected
   * item is visible onscreen.
   * @internal
   */
  @Prop({ mutable: true }) parentScrollContainerRef: HTMLElement;

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "sidebarMenu" is now deprecated. Instead, use "menuType"
   * Prop with value "sidebar". -- Is the Tree Menu displayed within a
   * Sidebar? If "true", adds negative left/right margin that offsets
   * the Sidebar's default padding & creates a seamless, "edge-to-edge"
   * Sidebar menu.
   * @deprecated
   */
  @Prop({ mutable: true }) sidebarMenu: boolean;

  @Watch('sidebarMenu')
  setMenuType(): void {
    !!this.sidebarMenu && (this.menuType = FluidMenuType.SIDEBAR);
  }
}
